/**
 * Optional focus carried into a workflow URL alongside the partition
 * coordinates. The Board view reads <c>itemId</c> as its rail selection
 * and <c>threadId</c> as the active thread on the selected task; both
 * are scoped to the Board view and ignored by the chat view.
 */
export interface WorkflowSelection {
    readonly itemId?: string;
    readonly threadId?: string;
    /** Open the board task composer on arrival (`compose=task`). */
    readonly compose?: "task";
}

/**
 * Builds the query string for a workflow detail URL. Single source of
 * truth for the format consumed by <c>/workflows/:id?…</c>:
 *
 *   - <c>entityType</c> + <c>entityId</c> are required (the backend's
 *     Cosmos partition lookup needs both).
 *   - <c>queueId</c> rides along opportunistically so any workflow-to-
 *     workflow navigation preserves the user's selected queue context
 *     for the @-mention composer.
 *   - <c>selection</c> deep-links the Board view's rail focus and
 *     active thread when the navigation already knows which item /
 *     thread the user should land on (e.g. zooming out from a child
 *     thread to its spawning task in the parent).
 *
 * Returns just the query string body (no leading <c>?</c>); callers
 * compose the final URL as
 * <c>/workflows/${id}?${buildWorkflowSearch(...)}</c>.
 */
export function buildWorkflowSearch(
    entityType: string,
    entityId: string,
    queueId?: string | null,
    selection?: WorkflowSelection,
): string {
    const params = new URLSearchParams({ entityType, entityId });
    if (queueId !== undefined && queueId !== null && queueId.length > 0) {
        params.set("queueId", queueId);
    }
    if (selection !== undefined) {
        if (selection.itemId !== undefined && selection.itemId.length > 0) {
            params.set("item", selection.itemId);
        }
        if (selection.threadId !== undefined && selection.threadId.length > 0) {
            params.set("thread", selection.threadId);
        }
        if (selection.compose === "task") {
            params.set("compose", "task");
        }
    }
    return params.toString();
}
